import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  LayoutDashboard, Home, FileBarChart, 
  Users, LogOut, ChevronLeft, ChevronRight, Settings
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LogoMark } from '@/components/Logo';
import { useState } from 'react';
import { cn } from '@/lib/utils';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/properties', label: 'Properties', icon: Home },
  { path: '/reports', label: 'Reports', icon: FileBarChart },
  { path: '/contacts', label: 'Contacts', icon: Users },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const isActive = (path) => {
    if (path === '/properties') {
      return location.pathname === '/properties' || location.pathname.startsWith('/properties/');
    }
    return location.pathname === path;
  };

  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 h-screen bg-white border-r border-line flex flex-col z-50 transition-all duration-300",
        collapsed ? "w-[72px]" : "w-64"
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className={cn(
        "h-16 border-b border-line flex items-center px-4",
        collapsed ? "justify-center" : "justify-between"
      )}>
        <Link to="/dashboard" className="flex items-center gap-2.5">
          <LogoMark size={36} />
          {!collapsed && (
            <span className="text-lg font-bold font-display tracking-tight">
              <span className="text-ink">Portfolio</span>
              <span className="text-signal-blue"> Pal</span>
            </span>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors duration-150",
                active 
                  ? "bg-anchor-blue text-white" 
                  : "text-muted-brand hover:bg-cloud hover:text-ink",
                collapsed && "justify-center"
              )}
              title={collapsed ? item.label : undefined}
            >
              <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={2} />
              {!collapsed && (
                <span className="text-sm font-medium">{item.label}</span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User Section */}
      <div className="border-t border-line p-3">
        {!collapsed && (
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 bg-signal-blue/10 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-signal-blue">
                {user?.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ink truncate">{user?.name}</p>
              <p className="text-xs text-muted-brand truncate">{user?.email}</p>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          onClick={logout}
          data-testid="logout-btn"
          className={cn(
            "w-full text-muted-brand hover:text-ink hover:bg-cloud",
            collapsed ? "justify-center px-2" : "justify-start"
          )}
        >
          <LogOut className="w-5 h-5" strokeWidth={2} />
          {!collapsed && <span className="ml-3 text-sm">Logout</span>}
        </Button>
      </div>

      {/* Collapse Toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 bg-white border border-line rounded-full flex items-center justify-center shadow-sm hover:bg-cloud transition-colors"
        data-testid="sidebar-toggle"
      >
        {collapsed ? (
          <ChevronRight className="w-4 h-4 text-muted-brand" />
        ) : (
          <ChevronLeft className="w-4 h-4 text-muted-brand" />
        )}
      </button>
    </aside>
  );
};

export default Sidebar;
